import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTORS = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Traps keyboard focus within a container element while active.
 * Restores focus to the previously focused element on deactivation.
 *
 * @param {React.RefObject<HTMLElement>} containerRef - ref to the trap container
 * @param {boolean} isActive - whether the trap is currently active
 */
export function useFocusTrap(containerRef, isActive) {
  const previouslyFocusedRef = useRef(null);

  useEffect(() => {
    if (!isActive) return undefined;

    previouslyFocusedRef.current = document.activeElement;

    const container = containerRef.current;
    if (!container) return undefined;

    const getFocusable = () =>
      Array.from(container.querySelectorAll(FOCUSABLE_SELECTORS)).filter(
        (el) => el instanceof HTMLElement && el.offsetParent !== null,
      );

    const focusable = getFocusable();
    const target = focusable.length > 0 ? focusable[0] : container;
    if (target && typeof target.focus === 'function') {
      target.focus();
    }

    const handleKeyDown = (event) => {
      if (event.key !== 'Tab') return;

      const currentFocusable = getFocusable();

      if (currentFocusable.length === 0) {
        event.preventDefault();
        container.focus();
        return;
      }

      const first = currentFocusable[0];
      const last = currentFocusable[currentFocusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey) {
        if (active === first || !currentFocusable.includes(active)) {
          event.preventDefault();
          last.focus();
        }
      } else if (active === last || !currentFocusable.includes(active)) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      const prev = previouslyFocusedRef.current;
      if (prev && typeof prev.focus === 'function') {
        prev.focus();
      }
    };
  }, [isActive, containerRef]);
}
